<!--
  Screen:    Pair with practice — code-entry dialog
  Route:     n/a (dialog overlay, opened from LaunchLayout's pair button)
  Layout:    LaunchLayout
  Role:      Patient
  Status:    ✅ Complete
  Inventory: docs/screen-inventory.md#pair-dialog
-->
<template>
  <Dialog v-model:show="show" variant="medium" closeable blur>
    <!-- ── Entry / error state ───────────────────────────────────────── -->
    <template v-if="!linked">
      <h1>{{ t('title') }}</h1>
      <p class="subtitle">{{ t('subtitle') }}</p>

      <div class="otp" role="group" :aria-label="t('title')">
        <input
          v-for="(_, i) in chars"
          :key="i"
          :ref="setRef(i)"
          :value="chars[i]"
          class="slot"
          type="text"
          inputmode="text"
          maxlength="1"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="characters"
          spellcheck="false"
          :aria-label="`${t('digit')} ${i + 1}`"
          @input="onInput(i, $event)"
          @keydown="onKeydown(i, $event)"
          @paste.prevent="onPaste"
        />
      </div>

      <p v-if="hasError" class="error" role="alert">{{ t('error') }}</p>

      <div class="footer">
        <button
          class="submit"
          :disabled="!isFull || submitting"
          @click="submit"
        >
          <img :src="linkIcon" aria-hidden="true" />
          <span>{{ t('button') }}</span>
        </button>
      </div>
    </template>

    <!-- ── Success state ─────────────────────────────────────────────── -->
    <template v-else>
      <GarlandBanner />

      <p class="success">{{ t('success') }}</p>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { useHomeworkStore } from '@/stores/homework'
import Dialog from '@/components/Dialog.vue'
import GarlandBanner from '@/components/GarlandBanner.vue'
import { celebrate } from '@/lib/confetti'
import linkIcon from '@/assets/icon/link.svg?no-inline'

const { t } = useI18n()
const auth = useAuthStore()

const show = defineModel('show', { required: true, type: Boolean, default: false })

const CODE_LENGTH = 6

// Plain array — reactivity not needed, we only call .focus()
const slotEls: HTMLInputElement[] = []
function setRef(i: number) {
  return (el: unknown) => {
    if (el instanceof HTMLInputElement) slotEls[i] = el
  }
}

const chars = ref<string[]>(Array(CODE_LENGTH).fill(''))
const hasError = ref(false)
const linked = ref(false)
const submitting = ref(false)

const isFull = computed(() => chars.value.every(c => c !== ''))

// ── OTP input handlers ───────────────────────────────────────────────────────

const VALID = /[^0-9A-HJKMNPQRSTVWXYZ]/g

function onInput(i: number, event: Event): void {
  const input = event.target as HTMLInputElement
  const char = input.value.toUpperCase().replace(VALID, '').slice(-1)
  chars.value[i] = char
  // Sync DOM to prevent Vue and the browser from fighting over the value.
  input.value = char
  hasError.value = false
  if (char && i < CODE_LENGTH - 1) slotEls[i + 1]?.focus()
}

function onKeydown(i: number, event: KeyboardEvent): void {
  if (event.key === 'Backspace') {
    if (chars.value[i]) {
      chars.value[i] = ''
      ;(event.target as HTMLInputElement).value = ''
    } else if (i > 0) {
      slotEls[i - 1]?.focus()
    }
  } else if (event.key === 'ArrowLeft' && i > 0) {
    event.preventDefault()
    slotEls[i - 1]?.focus()
  } else if (event.key === 'ArrowRight' && i < CODE_LENGTH - 1) {
    event.preventDefault()
    slotEls[i + 1]?.focus()
  }
}

function onPaste(event: ClipboardEvent): void {
  const text = event.clipboardData?.getData('text') ?? ''
  const clean = text.toUpperCase().replace(VALID, '').slice(0, CODE_LENGTH)
  chars.value = Array(CODE_LENGTH).fill('').map((_, i) => clean[i] ?? '')
  const focusAt = Math.min(clean.length, CODE_LENGTH - 1)
  void nextTick(() => slotEls[focusAt]?.focus())
}

// ── Submit ───────────────────────────────────────────────────────────────────

async function submit(): Promise<void> {
  if (!isFull.value || submitting.value) return
  submitting.value = true
  hasError.value = false
  try {
    const { data } = await api.post<{ practice_name: string | null }>(
      '/patient/link',
      { code: chars.value.join('') },
    )
    auth.practiceName = data.practice_name
    // The link is established whatever the practice happens to be called — a
    // nameless practice is still a practice, and deriving this from the name
    // is what used to leave the app showing "verknüpft" while behaving as if
    // nothing had happened.
    auth.linked = true
    linked.value = true
    // The therapist's Portal dialog fires the same confetti when it sees this
    // redemption, so both sides of the pairing celebrate it together.
    void celebrate()
    // Homework the therapist assigned before the link was merged onto this
    // device's account by the request that just returned. Fetch it now: the
    // store may hold an empty list from before the link, which its freshness
    // window would otherwise keep serving until the app restarts.
    void useHomeworkStore().refresh()
  } catch {
    hasError.value = true
  } finally {
    submitting.value = false
  }
}

// ── Lifecycle ────────────────────────────────────────────────────────────────

watch(show, async (open) => {
  if (open && !linked.value) {
    await nextTick()
    slotEls[0]?.focus()
  }
  if (!open) {
    chars.value = Array(CODE_LENGTH).fill('')
    hasError.value = false
    linked.value = false
  }
})
</script>

<style scoped lang="css">
/* ── Title ─────────────────────────────────────────────────────────────────── */
/* Center to match the OTP row. Dialog's :slotted(h1) defaults to left. */
h1 {
  text-align: center;
  margin-bottom: 0.75rem;
}

/* ── Subtitle ──────────────────────────────────────────────────────────────── */
.subtitle {
  font-size: 1rem !important;
  opacity: 0.6;
  text-align: center;
  /* Leave a generous gap before the OTP — it is the focal element. */
  margin-bottom: 2rem !important;
}

/* ── OTP row ───────────────────────────────────────────────────────────────── */
.otp {
  display: flex;
  justify-content: center;
  /* Wider gap so individual digits read clearly at arm's length on a tablet. */
  gap: 1.75rem;

  /* Subtitle carries the top spacing; OTP owns the bottom gap to the button. */
  margin: 0 0 2.25rem;
}

.slot {
  width: 2.75rem;

  font-family: inherit;
  font-size: 2rem;
  font-weight: 700;
  text-align: center;

  background: transparent;
  border: none;
  border-bottom: 3px solid var(--black);
  border-radius: 0;
  outline: none;

  padding: 0.35rem 0;

  caret-color: rgba(0, 142, 207, 1);
}

.slot:focus {
  border-bottom-color: rgba(0, 142, 207, 1);
}

/* ── Error ─────────────────────────────────────────────────────────────────── */
.error {
  font-size: 0.9rem !important;
  color: rgba(200, 40, 40, 1);
  text-align: center;
  margin-top: 0 !important;
  margin-bottom: 0.75rem !important;
}

/* ── Footer / submit button ────────────────────────────────────────────────── */
.footer {
  display: flex;
  justify-content: flex-end;

  /* OTP already holds the bulk of the bottom space; this just breathes. */
  margin-top: 0.5rem;
}

.submit {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  font-family: inherit;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--white);

  background: rgba(0, 142, 207, 1);
  border: none;
  border-radius: 15px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);

  padding: 1.15ch 1.35ch;
  cursor: pointer;

  img {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:disabled {
    background: rgba(170, 170, 170, 1);
    box-shadow: none;
    cursor: not-allowed;
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(:disabled):hover {
      background: var(--white);
      color: var(--black);

      /* The link.svg has white strokes — invert on light background hover. */
      img {
        filter: invert(1);
      }
    }
  }
}

/* ── Success state ─────────────────────────────────────────────────────────── */

/* Push the success text below the garland. The garland's height at the
   medium dialog width (~660px) is approx 82/691×660 ≈ 78px. The
   dialog-body already has padding-top: 4rem (64px) when closeable, so
   we add a modest extra margin to clear the remaining ~14px overlap and
   give the text room to breathe. */
.success {
  font-size: 1.5rem !important;
  font-weight: 700;
  text-align: center;

  /* Garland height ≈ 82/691 × dialog_width. At the medium dialog (~660px)
     that's ~78px; dialog-body padding-top is 4rem (64px), leaving ~14px
     of overlap. The 3rem here clears the garland comfortably at all sizes. */
  margin-top: 3rem !important;
  margin-bottom: 2rem !important;
}

/* ── Phone ─────────────────────────────────────────────────────────────────── */
/* The tablet OTP is tuned for arm's length — 2.75rem slots with 1.75rem gaps
   total ≈ 400px, which overflows the ~330px-wide dialog on a phone. Scale the
   slots and gaps to the viewport so all six always fit (down to a 320px phone);
   the >600px tablet keeps its generous sizing untouched. Also tighten the
   submit button + reduce the subtitle gap so the whole dialog fits comfortably. */
@media (max-width: 600px) {
  .subtitle {
    margin-bottom: 1.5rem !important;
  }

  .otp {
    gap: clamp(0.25rem, 2vw, 1rem);
    margin-bottom: 1.75rem;
  }

  .slot {
    width: clamp(1.9rem, 11.5vw, 2.75rem);
    font-size: clamp(1.35rem, 6vw, 2rem);
  }

  .submit {
    font-size: 1.1rem;
  }
}
</style>

<i18n>
{
  "de": {
    "title": "Gib hier den Code zum Verbinden ein.",
    "subtitle": "Du erhältst den Code von deiner therapeutischen Praxis.",
    "digit": "Stelle",
    "button": "verknüpfen",
    "error": "Der Code wurde falsch eingegeben. Bitte versuche es erneut.",
    "success": "Erfolgreich verknüpft!"
  },
  "en": {
    "title": "Enter the code to connect.",
    "subtitle": "You'll receive the code from your therapeutic practice.",
    "digit": "Digit",
    "button": "connect",
    "error": "The code was entered incorrectly. Please try again.",
    "success": "Successfully connected!"
  }
}
</i18n>
